/** @jsx jsx */
import { jsx } from 'theme-ui'
import { Label, Field, Button } from '../../../atoms'
import React, { Fragment, useEffect, useState } from 'react'
import ProgressCircle from '@uswitch/ev-components/lib/components/elements/progress-circle'
import classNames from 'classnames'
import { FsCore } from '../../../types/fs-core'
import { merge } from 'lodash-es'
import dayjs from '../../../utils/date'

export interface BusinessDetails {
  companyName?: string
  companyNumber?: string
  address?: any
  isExistingBusiness?: 'yes' | 'no'
  email?: string
  businessType?: string
  children?: React.ReactNode
  numberOfEmployees?: string
}

const addressLabels = {
  address_line_1: {
    label: 'Address Line 1',
    validation: {
      required: { value: true, message: 'Please enter your address' },
      maxLength: {
        value: 50,
        message: 'Maximum 50 character address',
      },
    },
  },
  address_line_2: {
    label: 'Address Line 2',
    validation: {
      maxLength: {
        value: 50,
        message: 'Maximum 50 character address',
      },
    },
  },
  postal_code: {
    label: 'Postcode',
    validation: {
      required: { value: true, message: 'Please enter your postcode' },
      pattern: {
        value: /^[a-zA-Z][a-zA-Z]?[0-9]([a-zA-Z]|[0-9])? ?[0-9][a-zA-Z]{2}$/,
        message: 'please enter a valid postcode e.g  SW1A 1AA', // JS only: <p>error message</p> TS only support string
      },
    },
  },
  locality: {
    label: 'Locality',
    validation: { required: { value: true, message: 'Please enter your locality' } },
  },
  country: {
    label: 'Country',
    validation: { required: { value: true, message: 'Please enter your country' } },
  },
}

const editModeFields = {
  companyName: {
    label: 'Name',
    validation: { required: { value: true, message: 'Please enter your business name' } },
  },
  companyNumber: { label: 'Company Number', validation: {} },
  ...addressLabels,
}

const EditMode = ({ businessDetails, children, register, setValue, errors, soleTrader }) => {
  let fields = Object.keys(editModeFields)

  if (soleTrader) {
    fields = fields.filter((f) => f !== 'companyNumber')
  }

  return (
    <div className='SMEform__companyDetails__detailsBox'>
      {fields.map((field, index) => {
        return (
          <Field key={index} stack={true}>
            <Label htmlFor={field}>{editModeFields[field].label}</Label>
            <input
              type='text'
              id={field}
              value={businessDetails?.[field]}
              onChange={(e) => {
                setValue(`businessDetails.${field}`, e.target.value)
              }}
              {...register(`businessDetails.${field}`, { ...editModeFields[field].validation })}
            />
            {errors?.businessDetails?.[field] && (
              <div className='SMEform__error'>{errors.businessDetails[field].message}</div>
            )}
          </Field>
        )
      })}
      {children}
    </div>
  )
}

const BusinessFinder = ({
  businessDetails,
  brand,
  disable = false,
  componentData,
  children,
  register,
  setValue,
  errors,
  soleTrader = false,
}: {
  businessDetails: {} | BusinessDetails
  children: React.ReactNode
  brand: FsCore.FSBrands
}) => {
  const findBusiness = (e) => {
    setLoading(true)
    e.preventDefault()
    const apiPath: string = __FS_API_URL__

    if (!companyNameInput) {
      setError('Please fill in your business name')
      setLoading(false)
      return
    }

    fetch(`${apiPath}/company/${companyNameInput}`)
      .then((response) => response.json())
      .then((response: { data: BusinessDetails[] }) => {
        setLoading(false)
        if (response.data.length < 1) {
          setEditMode(true)
          setError("We couldn't find any results for that name. Please fill in your details below")
          setCompanies(response.data)
        } else {
          setError(undefined)
          setCompanies(response.data)
        }
      })
      .catch(() => {
        setError('oops, something went wrong. please try again later')
        setLoading(false)
      })
  }
  const [companyNameInput, setCompanyNameInput] = useState<null | string>(null)
  const [companies, setCompanies] = useState<null | BusinessDetails[]>(null)
  const [editMode, setEditMode] = useState(soleTrader)
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setEditMode(soleTrader)
  }, [soleTrader])

  return (
    <Field
      className={classNames('SMEform__companyDetails', {
        'SMEform__companyDetails--disable': disable,
      })}
    >
      <Label htmlFor='businessName'>Enter your business details</Label>
      <div className='SMEform__input SMEform__companyDetails__businessSearchBox'>
        {!soleTrader && (
          <Fragment>
            <div>We use Companies House data to search your company&apos;s details.</div>
            <input
              type='text'
              id='businessName'
              placeholder='Business name'
              onChange={(e) => {
                setCompanyNameInput(e.target.value)
                setValue('companyNameInput', e.target.value)
              }}
              {...register('companyNameInput', { required: !editMode })}
            />
            {errors?.companyNameInput && (
              <div className='SMEform__companyDetails__error'>
                Please enter your company details
              </div>
            )}
            {error && <div className='SMEform__companyDetails__error'>{error}</div>}
            <div className='SMEform__companyDetails__cta'>
              <Button
                variant={brand === 'money' ? 'secondary' : 'tertiary'}
                size='md'
                onClick={findBusiness}
                dataClickTracking={{
                  ...componentData,
                  additionalProps: {
                    formStep: 'find_business',
                    businessName: companyNameInput,
                  },
                }}
              >
                Find Business
              </Button>
              <Button
                variant={'tertiary'}
                size='sm'
                onClick={() => {
                  setEditMode(true)
                }}
                dataClickTracking={{
                  ...componentData,
                  additionalProps: {
                    formStep: 'find_business_not_registered',
                    businessName: companyNameInput,
                  },
                }}
              >
                Not Registered
              </Button>

              {loading && <ProgressCircle sx={{ maxHeight: '32px' }} />}
            </div>

            {companies && companies.length > 0 && (
              <select
                onChange={(e) => {
                  const company = companies.find((c) => c.companyName === e.target.value)
                  if (company) {
                    const flatCompany = merge(company, company.address)

                    Object.keys(flatCompany).forEach((k) => {
                      if (k === 'date_of_creation') {
                        const nowDate = dayjs()
                        const existenceTime = nowDate.diff(dayjs(flatCompany[k]), 'year')
                        const existenceTimeString =
                          existenceTime < 1
                            ? 'less than 1 year'
                            : existenceTime < 5
                            ? '1-5 years'
                            : '5+ years'
                        register('businessDetails.existenceTime')
                        setValue('businessDetails.existenceTime', existenceTimeString)
                      } else {
                        register(`businessDetails.${k}`)
                        setValue(`businessDetails.${k}`, flatCompany[k])
                      }
                    })
                  } else {
                    setEditMode(true)
                  }
                }}
                name='businesses'
              >
                <option>Please select your business</option>
                <option>My business isnt listed here</option>
                {companies.map((c, index) => (
                  <option key={index}>{c.companyName}</option>
                ))}
              </select>
            )}
            {businessDetails.companyName && !editMode && (
              <div className='SMEform__companyDetails__detailsBox'>
                <div>
                  <div>
                    <p className='SMEform__companyDetails__detailsBox--label'>Company name</p>
                    <p>{businessDetails.companyName}</p>
                  </div>
                  <div>
                    <p className='SMEform__companyDetails__detailsBox--label'>
                      Registered company number
                    </p>
                    <p>{businessDetails.companyNumber}</p>
                  </div>
                  <div>
                    <p className='SMEform__companyDetails__detailsBox--label'>Address</p>
                    <p>{businessDetails.addressSnippet}</p>
                  </div>
                  <div>
                    <Button
                      variant='tertiary'
                      size='sm'
                      onClick={(e: { preventDefault: () => void }) => {
                        e.preventDefault()
                        setEditMode(true)
                      }}
                    >
                      Edit Details
                    </Button>
                  </div>
                  {children}
                </div>
              </div>
            )}
          </Fragment>
        )}
        {editMode && (
          <EditMode
            businessDetails={businessDetails}
            register={register}
            setValue={setValue}
            errors={errors}
            soleTrader={soleTrader}
          >
            {children}
          </EditMode>
        )}
      </div>
    </Field>
  )
}

export default BusinessFinder
